import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import * as XLSX from "xlsx";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import wqmsService from "../services/wqmsService";
import "./RiverParameterComparison.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Karachi";

const RIVER_OPTIONS = ["All", "Ravi", "Chenab", "Jhelum", "Sutlej", "Indus", "Punjnad"];
const DRAIN_OPTIONS = ["All", "Yes", "No"];

const intervals = [
  { value: "hourly", label: "Hourly" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

// Base params (MEASURED keys)
const PARAM_BASE = [
  { key: "ph_avg", label: "pH" },
  { key: "temperature_c_avg", label: "Temperature (°C)" },
  { key: "dissolved_oxygen_mg_l_avg", label: "Dissolved Oxygen (mg/L)" },
  { key: "turbidity_ntu_avg", label: "Turbidity (NTU)" },
  { key: "conductivity_us_cm_avg", label: "Conductivity (µS/cm)" },
  //{ key: "level_m_avg", label: "Level (m) (avg)" },
  { key: "redox_mv_avg", label: "Redox/ORP (mV)" },

  { key: "ammonia_mg_l_avg", label: "Ammonia (NH3) (mg/L)" },
  { key: "bod_mg_l_avg", label: "BOD (mg/L)" },
  { key: "cod_mg_l_avg", label: "COD (mg/L)" },

  { key: "tss_mg_l_avg", label: "TSS (mg/L)" },
  { key: "toc_mg_l_avg", label: "TOC (mg/L)" },
  { key: "doc_mg_l_avg", label: "DOC (mg/L)" },
  { key: "chloride_mg_l_avg", label: "Chloride (mg/L)" },
  { key: "color_hazen_avg", label: "Color (Hazen)" },
];

const toCleanKey = (k) => {
  if (!k) return k;
  if (k.endsWith("_clean_avg")) return k;
  return k.replace(/_avg$/, "_clean_avg");
};

const toMeasuredKey = (k) => {
  if (!k) return k;
  if (k.endsWith("_avg") && !k.endsWith("_clean_avg")) return k;
  return k.replace(/_clean_avg$/, "_avg");
};

const cleanParamLabel = (labelOrKey) => {
  return String(labelOrKey || "")
    .replace(/_clean_avg/gi, "")
    .replace(/_avg/gi, "")
    .replace(/\s*\(clean\s*avg\)/gi, "")
    .replace(/\s*\(avg\)/gi, "")
    .trim();
};
// ---- Reliable browser download (avoids XLSX.writeFile issues) ------------
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
};

// ---- Excel export: one sheet per date, with 24 hour columns --------------
const exportHourlyExcelByDate = async ({
  stationIds,
  paramKeys,
  startISO,
  endISO,
  stationNameByKey,
  paramLabelByKey,
}) => {
  if (!stationIds?.length) throw new Error("Select at least 1 station.");
  if (!paramKeys?.length) throw new Error("Select at least 1 parameter.");

  const hourCols = Array.from({ length: 24 }, (_, h) => `H${String(h).padStart(2, "0")}`);

  // Fetch HOURLY data for each parameter (export must be hourly)
  const seriesHourlyByParam = {};
  for (const paramKey of paramKeys) {
    const rows = await wqmsService.getSeriesMultiStations({
      stationIds,
      param: paramKey,
      start: startISO,
      end: endISO,
      res: "hourly",
    });

    // Normalize into { ts, S-<id>: value }
    const map = new Map();
    for (const r of rows || []) {
      const ts = r.ts;
      if (!ts) continue;
      if (!map.has(ts)) map.set(ts, { ts });
      map.get(ts)[`S-${r.station_id}`] = r.value;
    }

    seriesHourlyByParam[paramKey] = Array.from(map.values()).sort(
      (a, b) => new Date(a.ts) - new Date(b.ts)
    );
  }

  // Build date list inclusive
  const startD = dayjs(startISO).format("YYYY-MM-DD");
  const endD = dayjs(endISO).format("YYYY-MM-DD");

  const dates = [];
  let cur = dayjs(startD);
  const last = dayjs(endD);
  while (cur.isBefore(last, "day") || cur.isSame(last, "day")) {
  dates.push(cur.format("YYYY-MM-DD"));
  cur = cur.add(1, "day");
}


  const wb = XLSX.utils.book_new();

  for (const dateStr of dates) {
    // row key: paramKey||stationId
    const rowsMap = new Map();

    const ensureRow = (paramKey, stationId) => {
      const key = `${paramKey}||${stationId}`;
      if (!rowsMap.has(key)) {
        const row = {
          Parameter: paramLabelByKey[paramKey] || paramKey,
          Station_ID: stationId,
          Station_Name: stationNameByKey[`S-${stationId}`] || `Station ${stationId}`,
        };
        hourCols.forEach((c) => (row[c] = ""));
        rowsMap.set(key, row);
      }
      return rowsMap.get(key);
    };

    for (const paramKey of paramKeys) {
      const series = seriesHourlyByParam[paramKey] || [];

      for (const point of series) {
        if (!point?.ts) continue;

        // Treat backend ts as "naive PKT" even if it includes Z
        const d = dayjs.utc(point.ts).tz(TZ, true);
        if (d.format("YYYY-MM-DD") !== dateStr) continue;

        const hourCol = `H${d.format("HH")}`;

        for (const stationId of stationIds) {
          const k = `S-${stationId}`;
          if (!(k in point)) continue;

          const v = point[k];
          const row = ensureRow(paramKey, stationId);
          row[hourCol] = v === null || v === undefined ? "" : v;
        }
      }
    }

    const sheetRows = Array.from(rowsMap.values()).sort((a, b) => {
      const p = String(a.Parameter).localeCompare(String(b.Parameter));
      if (p) return p;
      return Number(a.Station_ID) - Number(b.Station_ID);
    });

    const aoa = [
      ["Parameter", "Station_ID", "Station_Name", ...hourCols],
      ...sheetRows.map((r) =>
        ["Parameter", "Station_ID", "Station_Name", ...hourCols].map((k) => r[k])
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Sheet name max 31 chars
    const sheetName = dayjs(dateStr).format("DD-MM-YYYY").slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  }

  // ✅ Browser-safe write + download
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const filename = `Hourly_Params_DailyTabs_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`;
  downloadBlob(blob, filename);
};

export default function RiverParameterComparison() {
  const navigate = useNavigate();

  const [river, setRiver] = useState("All");
  const [drain, setDrain] = useState("All");

  const [stations, setStations] = useState([]);
  const [selectedStations, setSelectedStations] = useState([]);

  //const [valueMode, setValueMode] = useState("clean"); // default clean
  const [valueMode] = useState("measured"); // fixed as measured
  //const [selectedParameters, setSelectedParameters] = useState([toCleanKey("ph_avg")]);
  const [selectedParameters, setSelectedParameters] = useState(["ph_avg"]);

  const [interval, setInterval] = useState("hourly");
  const [chartType, setChartType] = useState("line"); // default line

  const [fromDate, setFromDate] = useState(dayjs().tz(TZ).subtract(2, "day").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().tz(TZ).format("YYYY-MM-DD"));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [seriesByParam, setSeriesByParam] = useState({}); // { paramKey: [{ts, S-1:val, ...}] }

  // Param list depends on mode
 /* const PARAM_OPTIONS = useMemo(() => {
    if (valueMode === "clean") {
      return PARAM_BASE.map((p) => ({
        key: toCleanKey(p.key),
        label: p.label.replace("", ""),
      }));
    }
    return PARAM_BASE;
  }, [valueMode]);
*/

  const PARAM_OPTIONS = useMemo(() => {
    const mapped =
      valueMode === "clean"
        ? PARAM_BASE.map((p) => ({
            key: toCleanKey(p.key),
            label: cleanParamLabel(p.label),
          }))
        : PARAM_BASE.map((p) => ({
            key: p.key,
            label: cleanParamLabel(p.label),
          }));

    return mapped;
  }, [valueMode]);

  const paramLabelByKey = useMemo(() => {
    const m = {};

    for (const p of PARAM_OPTIONS) {
      m[p.key] = cleanParamLabel(p.label);
      m[toMeasuredKey(p.key)] = cleanParamLabel(p.label);
      m[toCleanKey(p.key)] = cleanParamLabel(p.label);
    }

    return m;
  }, [PARAM_OPTIONS]);

  // Send timestamps WITHOUT Z/+05 (DB is timestamp without tz stored as PKT)
  const startISO = useMemo(() => `${fromDate}T00:00:00.000`, [fromDate]);
  const endISO = useMemo(() => `${toDate}T23:59:59.999`, [toDate]);

  // Parse backend ts as "naive PKT" even if it includes Z
  const fmtTsPKT = (ts, fmt) => {
    if (!ts) return "";
    return dayjs.utc(ts).tz(TZ, true).format(fmt); // keepLocalTime=true
  };

  // Load stations
  useEffect(() => {
    (async () => {
      try {
        setError("");
        const list = await wqmsService.getStations({
          timeRange: "all",
          force: true,
          river,
          drain,
        });
        setStations(list || []);
        setSelectedStations([]);
      } catch (e) {
        console.error(e);
        setError("Failed to load stations.");
      }
    })();
  }, [river, drain]);

  const stationNameByKey = useMemo(() => {
    const m = {};
    for (const s of stations) {
      const id = Number(s.station_id ?? s.id);
      m[`S-${id}`] = s.site_name || s.name || `Station ${id}`;
    }
    return m;
  }, [stations]);

  const lineKeys = useMemo(() => selectedStations.map((id) => `S-${id}`), [selectedStations]);

  // Colors
  const STATION_COLORS = [
    "#60a5fa",
    "#34d399",
    "#f472b6",
    "#fbbf24",
    "#a78bfa",
    "#22d3ee",
    "#fb7185",
    "#4ade80",
    "#e879f9",
    "#fde047",
  ];

  const colorByLineKey = useMemo(() => {
    const m = {};
    lineKeys.forEach((k, i) => (m[k] = STATION_COLORS[i % STATION_COLORS.length]));
    return m;
  }, [lineKeys]);

  // Legend (use series names)
  const CustomLegend = ({ payload = [] }) => {
    return (
      <div className="rpc-legend">
        {payload.map((item) => (
          <span key={item.dataKey} className="rpc-legendItem" style={{ color: item.color }}>
            {item.value}
          </span>
        ))}
      </div>
    );
  };

  // Tooltip (date visible)
  const CustomTooltip = ({ active, label, payload }) => {
    if (!active || !payload?.length) return null;
    const labelText = fmtTsPKT(label, "YYYY-MM-DD HH:mm");

    return (
      <div className="rpc-tooltip">
        <div className="rpc-tooltipDate">{labelText}</div>
        {payload.map((p) => (
          <div key={p.dataKey} className="rpc-tooltipRow" style={{ color: p.color }}>
            <span className="rpc-tooltipName">{p.name}</span>
            <span className="rpc-tooltipVal">
              {p.value === null || p.value === undefined ? "—" : p.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const fetchData = async () => {
    if (!selectedStations.length) return setError("Select at least 1 station.");
    if (!selectedParameters.length) return setError("Select at least 1 parameter.");

    setLoading(true);
    setError("");

    try {
      const out = {};

      for (const paramKey of selectedParameters) {
        const rows = await wqmsService.getSeriesMultiStations({
          stationIds: selectedStations,
          param: paramKey,
          start: startISO,
          end: endISO,
          res: interval,
        });

        const map = new Map();
        for (const r of rows || []) {
          const ts = r.ts;
          if (!ts) continue;
          if (!map.has(ts)) map.set(ts, { ts });
          map.get(ts)[`S-${r.station_id}`] = r.value;
        }

        out[paramKey] = Array.from(map.values()).sort((a, b) => new Date(a.ts) - new Date(b.ts));
      }

      setSeriesByParam(out);
    } catch (e) {
      console.error(e);
      setError("Failed to load comparison graph data.");
    } finally {
      setLoading(false);
    }
  };

  // Build multi charts list
  const charts = useMemo(() => {
    return selectedParameters.map((k) => ({
      key: k,
      label: cleanParamLabel(paramLabelByKey[k] || k),
      data: seriesByParam[k] || [],
    }));
  }, [selectedParameters, paramLabelByKey, seriesByParam]);

  return (
    <div className="rpc-page">
      <div className="rpc-wrap">
        <div className="rpc-header">
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="rpc-header-main">
            <h2 className="rpc-title">Parameters Comparison (River → Stations)</h2>
            <p className="rpc-subtitle">Compare multiple parameters across multiple stations (PKT).</p>
          </div>

          <div className="rpc-header-actions">
            <span className="rpc-pill">
              Selected: <strong>{selectedParameters.length}</strong>
            </span>
          </div>
        </div>

        {/* CONTROLS */}
        <div className="rpc-card rpc-controls">
          <div className="rpc-controls-head">Report filters</div>
          <div className="rpc-controlsGrid">
            {/* LEFT */}
            <div className="rpc-leftControls">
              <div className="rpc-leftGrid">
                <div className="rpc-field">
                  <label>Chart</label>
                  <select className="rpc-select" value={chartType} onChange={(e) => setChartType(e.target.value)}>
                    <option value="line">Line</option>
                    <option value="bar">Bar</option>
                  </select>
                </div>
{/*
                <div className="rpc-field">
                  <label>Values</label>
                  <select
                    className="rpc-select"
                    value={valueMode}
                    onChange={(e) => {
                      const next = e.target.value; // clean | measured
                      setValueMode(next);
                      setSelectedParameters((prev) =>
                        prev.map((k) => (next === "clean" ? toCleanKey(k) : toMeasuredKey(k)))
                      );
                      setSeriesByParam({});
                    }}
                  >
                    <option value="clean">Clean</option>
                    <option value="measured">Measured</option>
                  </select>
                </div>
*/}
                <div className="rpc-field">
                  <label>River</label>
                  <select className="rpc-select" value={river} onChange={(e) => setRiver(e.target.value)}>
                    {RIVER_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rpc-field">
                  <label>On drain</label>
                  <select className="rpc-select" value={drain} onChange={(e) => setDrain(e.target.value)}>
                    {DRAIN_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rpc-field">
                  <label>From</label>
                  <input className="rpc-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>

                <div className="rpc-field">
                  <label>To</label>
                  <input className="rpc-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="rpc-rightControls rpc-rightControls--stations">
              <div className="rpc-rightGrid">
                <div className="rpc-field">
                  <label>Stations (Multi)</label>
                  <select
                    className="rpc-multi rpc-multi-stations"
                    multiple
                    value={selectedStations.map(String)}
                    onChange={(e) => {
                      const ids = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
                      setSelectedStations(ids);
                    }}
                  >
                    {(stations || []).map((s) => {
                      const id = Number(s.station_id ?? s.id);
                      const name = s.site_name || s.name || `Station ${id}`;
                      return (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="rpc-field">
                  <label>Parameters (Multi)</label>
                  <select
                    className="rpc-multi rpc-multi-params"
                    multiple
                    value={selectedParameters}
                    onChange={(e) => {
                      const vals = Array.from(e.target.selectedOptions).map((o) => o.value);
                      setSelectedParameters(vals);
                    }}
                  >
                    {PARAM_OPTIONS.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rpc-field">
                  <label>Interval</label>
                  <select className="rpc-select" value={interval} onChange={(e) => setInterval(e.target.value)}>
                    {intervals.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="rpc-controls-footer">
            <button className="rpc-btn" onClick={fetchData} disabled={loading}>
              {loading ? "Loading..." : "Compare"}
            </button>
          </div>

          {error ? <div className="rpc-alert">{error}</div> : null}
        </div>

        {/* MULTI-PARAM CHARTS */}
        {charts.length === 0 ? (
          <div className="rpc-card rpc-empty">
            <strong>Select parameters and stations, then Compare.</strong>
          </div>
        ) : (
          charts.map((c) => (
            <div key={c.key} className="rpc-card rpc-chart" style={{ marginBottom: 14 }}>
              <div className="rpc-chartHeader">
                <div className="rpc-chartTitle">Parameter: {c.label}</div>
                <div className="rpc-pill rpc-mono">
                  {fromDate} 00:00 → {toDate} 23:59 (PKT)
                </div>
              </div>

              {c.data.length === 0 ? (
                <div className="rpc-empty">
                  <div>
                    <strong>No data for this parameter</strong>
                    <div style={{ marginTop: 6, opacity: 0.8 }}>Try bigger date range or a different interval.</div>
                  </div>
                </div>
              ) : (
                <div className="rpc-chartBox" style={{ height: 340 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {chartType === "line" ? (
                      <LineChart data={c.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="ts" tickFormatter={(v) => fmtTsPKT(v, "MM-DD HH:mm")} minTickGap={24} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        {lineKeys.map((k) => (
                          <Line
                            key={k}
                            type="monotone"
                            dataKey={k}
                            dot={false}
                            strokeWidth={2}
                            connectNulls
                            stroke={colorByLineKey[k]}
                            isAnimationActive={false}
                            name={stationNameByKey[k] || k}
                          />
                        ))}
                      </LineChart>
                    ) : (
                      <BarChart data={c.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="ts" tickFormatter={(v) => fmtTsPKT(v, "MM-DD HH:mm")} minTickGap={24} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        {lineKeys.map((k) => (
                          <Bar key={k} dataKey={k} fill={colorByLineKey[k]} name={stationNameByKey[k] || k} />
                        ))}
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
